import { bytesToBinary } from './bytes.js';

export type UnwrapErrorCode = 'NO_HTML_PART' | 'BAD_PLIST' | 'NO_MAIN_RESOURCE' | 'UNSUPPORTED';
export class UnwrapError extends Error {
  constructor(public readonly code: UnwrapErrorCode, message: string) { super(message); this.name = 'UnwrapError'; }
}

function parseHeaders(block: string): Record<string, string> {
  const out: Record<string, string> = {};
  const unfolded = block.replace(/\r?\n[ \t]+/g, ' ');
  for (const line of unfolded.split(/\r?\n/)) {
    const i = line.indexOf(':');
    if (i > 0) out[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
  }
  return out;
}

export function decodeQuotedPrintable(s: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    if (ch === 0x3d) { // '='
      if (s[i + 1] === '\r' && s[i + 2] === '\n') { i += 2; continue; }
      if (s[i + 1] === '\n') { i += 1; continue; }
      const hex = s.slice(i + 1, i + 3);
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) { out.push(parseInt(hex, 16)); i += 2; continue; }
    }
    out.push(ch & 0xff);
  }
  return new Uint8Array(out);
}

function decodeBase64(s: string): Uint8Array {
  const bin = atob(s.replace(/\s+/g, ''));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function charsetOf(contentType: string): string {
  const m = /charset="?([\w-]+)"?/i.exec(contentType);
  return m?.[1] ?? 'utf-8';
}

/** Chrome/Edge'in kaydettiği MHTML'den ilk text/html parçasını çözer. */
export function mhtmlToHtml(bytes: Uint8Array): string {
  const raw = bytesToBinary(bytes);
  const sep = raw.search(/\r?\n\r?\n/);
  if (sep < 0) throw new UnwrapError('NO_HTML_PART', 'MHTML başlıkları okunamadı');
  const top = parseHeaders(raw.slice(0, sep));
  const bm = /boundary="?([^";\r\n]+)"?/i.exec(top['content-type'] ?? '');
  if (!bm) throw new UnwrapError('NO_HTML_PART', 'MHTML sınır dizesi bulunamadı');
  const boundary = bm[1]!;
  const parts = raw.split('--' + boundary).slice(1);
  for (const part of parts) {
    if (part.startsWith('--')) break;
    const body0 = part.replace(/^\r?\n/, '');
    const s = body0.search(/\r?\n\r?\n/);
    if (s < 0) continue;
    const h = parseHeaders(body0.slice(0, s));
    const ct = h['content-type'] ?? '';
    if (!/^text\/html/i.test(ct)) continue;
    const body = body0.slice(s).replace(/^\r?\n\r?\n/, '').replace(/\r?\n$/, '');
    const enc = (h['content-transfer-encoding'] ?? '7bit').toLowerCase();
    let data: Uint8Array;
    if (enc === 'quoted-printable') data = decodeQuotedPrintable(body);
    else if (enc === 'base64') data = decodeBase64(body);
    else data = new Uint8Array(Array.from(body, (c) => c.charCodeAt(0) & 0xff));
    return new TextDecoder(charsetOf(ct)).decode(data);
  }
  throw new UnwrapError('NO_HTML_PART', 'MHTML içinde HTML parçası yok');
}
